/**
 * Pro paywall UI: the gate card, the early-access banner, the "Pro" badge and
 * the client-side delivery of Pro lesson sections.
 *
 * Security model: this module NEVER holds Pro content. The public catalog only
 * has each Pro lesson's free preview + locked section titles. The full sections
 * arrive from the Convex query `api.proLessons.body`, which returns them only
 * to a verified Pro identity, or to everyone while billing is NOT live
 * ("early access", the BILLING_LIVE switch in `convex/lib/billingConfig.ts`).
 * Everything here is rendering; SSR HTML is preview + gate in both modes.
 *
 * Render states of the gated container (`.pro-gated[data-state]`):
 *   pending   SSR + first client render (and, for anonymous visitors, until
 *             the body query answers). Holds BOTH the gate and the skeleton;
 *             CSS shows the skeleton when `<html data-auth-hint>` (signed in)
 *             or `<html data-billing="early">` (early access last time) is set
 *             and the gate otherwise. Identical for every visitor, so SSR
 *             stays cacheable and crawlers always see the preview + gate.
 *   loading   Signed in, Convex is resolving entitlement + body: skeleton.
 *   locked    Not Pro and billing live: the gate.
 *   unlocked  Pro (or early access, with a slim banner): the full sections.
 */
import { useAppAuth } from '../lib/auth/app-auth'
import { useConvexAuth, useQuery } from 'convex/react'
import { ArrowRight, Lock, Sparkles } from 'lucide-react'
import { Component, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../../convex/_generated/api'
import type { LessonSection } from '../content/catalog'
import { trackEvent } from '../lib/analytics'
import { FOUNDING_HASH } from '../lib/pricing-cta'
import { localizedPath, useTranslation } from '../lib/i18n'
import type { Locale } from '../lib/i18n'
import { PRO_GATED_CLASS, PRO_GATED_ID } from '../lib/paywall'
import { formatPrice, pricePerMonth } from '../lib/pricing'
import { useBillingStatus } from '../lib/use-billing'
import { ProBadge } from './pro-badge'

export { ProBadge } from './pro-badge'

/* ------------------------------ Data hook -------------------------------- */

export type ProLessonState =
  | { status: 'pending' }
  | { status: 'loading' }
  | { status: 'locked'; signedIn: boolean }
  | { status: 'unlocked'; sections: LessonSection[]; earlyAccess: boolean }

type BodyResult =
  | { status: 'unauthenticated' | 'locked' | 'not_found' }
  | {
      status: 'unlocked'
      sections: LessonSection[]
      access?: 'entitled' | 'early_access'
    }

/** True after the first client commit (keeps hydration identical to SSR). */
function useHydrated(): boolean {
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])
  return hydrated
}

function unlocked(result: BodyResult): ProLessonState | null {
  if (result.status !== 'unlocked') return null
  return {
    status: 'unlocked',
    sections: result.sections,
    earlyAccess: result.access === 'early_access',
  }
}

function useProLessonState(input: {
  locale: Locale
  courseSlug: string
  lessonSlug: string
}): ProLessonState {
  const hydrated = useHydrated()
  const { isLoaded: clerkLoaded, isSignedIn } = useAppAuth()
  const { isLoading: convexAuthLoading, isAuthenticated } = useConvexAuth()
  // Anonymous visitors query too: in early access the server answers with
  // the sections; when billing is live with `unauthenticated`.
  const canQuery =
    hydrated && clerkLoaded && (isSignedIn ? isAuthenticated : true)
  const result = useQuery(
    api.proLessons.body,
    canQuery
      ? {
          locale: input.locale,
          courseSlug: input.courseSlug,
          lessonSlug: input.lessonSlug,
        }
      : 'skip',
  ) as BodyResult | undefined

  if (!hydrated || !clerkLoaded) return { status: 'pending' }
  if (!isSignedIn) {
    // Keep the SSR placeholder (CSS picks gate vs skeleton) until answered.
    if (result === undefined) return { status: 'pending' }
    return unlocked(result) ?? { status: 'locked', signedIn: false }
  }
  // Signed in with Clerk; Convex is still exchanging / validating the JWT.
  if (convexAuthLoading) return { status: 'loading' }
  // Convex rejected the session token: fail closed.
  if (!isAuthenticated) return { status: 'locked', signedIn: true }
  if (result === undefined) return { status: 'loading' }
  return unlocked(result) ?? { status: 'locked', signedIn: true }
}

/* ------------------------------ Gate card -------------------------------- */

type GateTracking = { course: string; lesson: string }

export function ProGateCard({
  lockedSectionTitles,
  signedIn,
  tracking,
}: {
  lockedSectionTitles: string[]
  signedIn: boolean
  tracking?: GateTracking
}) {
  const { locale, t } = useTranslation()
  const price = formatPrice(pricePerMonth('pro', 'year'), locale)
  const track = (cta: 'unlock' | 'sign_in' | 'start_free') =>
    trackEvent('paywall_cta_clicked', { surface: 'pro_gate', cta, ...tracking })

  return (
    <aside className="pro-gate" aria-labelledby="pro-gate-title">
      <div className="pro-gate-spotlight" aria-hidden="true" />
      <div className="pro-gate-inner">
        <div className="pro-gate-copy">
          <span className="pro-gate-eyebrow">
            <Lock size={12} strokeWidth={2.5} aria-hidden="true" />
            {t('pro.gateEyebrow')}
          </span>
          <h2 id="pro-gate-title">{t('pro.gateTitle')}</h2>
          <p>{signedIn ? t('pro.gateSignedInText') : t('pro.gateText')}</p>
          <div className="pro-gate-actions">
            <a
              className="pro-gate-primary"
              href={localizedPath('pricing', locale)}
              onClick={() => track('unlock')}
            >
              {t('pro.unlockCta')}
              <ArrowRight size={16} aria-hidden="true" />
            </a>
            <a
              className="pro-gate-secondary"
              href={
                signedIn
                  ? localizedPath('course', locale, {
                      courseSlug: 'foundations',
                    })
                  : localizedPath('login', locale)
              }
              onClick={() => track(signedIn ? 'start_free' : 'sign_in')}
            >
              {signedIn ? t('pro.startFreeCta') : t('pro.signInCta')}
            </a>
          </div>
          <small className="pro-gate-note">
            {t('pro.priceNote', { price })}
          </small>
          {signedIn ? null : (
            <a
              className="pro-gate-free-link"
              href={localizedPath('course', locale, {
                courseSlug: 'foundations',
              })}
              onClick={() => track('start_free')}
            >
              {t('pro.startFreeCta')}
            </a>
          )}
        </div>
        {lockedSectionTitles.length > 0 ? (
          <div className="pro-gate-outline">
            <div className="pro-gate-outline-head">
              <strong>{t('pro.lockedHeading')}</strong>
              <span>
                {t('pro.lockedCount', { count: lockedSectionTitles.length })}
              </span>
            </div>
            <ol aria-label={t('pro.lockedSectionsLabel')}>
              {lockedSectionTitles.map((title, index) => (
                <li key={title}>
                  <span className="pro-gate-outline-index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="pro-gate-outline-title">{title}</span>
                  <Lock size={13} aria-hidden="true" />
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>
    </aside>
  )
}

/* -------------------------- Early-access banner --------------------------- */

/** Slim banner above an early-access Pro lesson (billing not live yet). */
export function EarlyAccessBanner({ tracking }: { tracking?: GateTracking }) {
  const { locale, t } = useTranslation()
  return (
    <aside className="early-access-banner" aria-label={t('pro.earlyAccessLabel')}>
      <span className="early-access-tag">
        <Sparkles size={12} aria-hidden="true" />
        {t('pro.earlyAccessLabel')}
      </span>
      <p>{t('pro.earlyAccessText')}</p>
      <a
        className="early-access-cta"
        href={`${localizedPath('pricing', locale)}${FOUNDING_HASH}`}
        onClick={() =>
          trackEvent('paywall_cta_clicked', {
            surface: 'early_access_banner',
            cta: 'join_founding',
            ...tracking,
          })
        }
      >
        {t('pro.earlyAccessCta')}
        <ArrowRight size={15} aria-hidden="true" />
      </a>
    </aside>
  )
}

/* -------------------------------- Skeleton -------------------------------- */

export function ProLessonSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="pro-skeleton" role="status" aria-live="polite">
      <span className="sr-only">{t('pro.loadingLabel')}</span>
      {[0, 1, 2].map((block) => (
        <div className="pro-skeleton-section" key={block} aria-hidden="true">
          <span className="pro-skeleton-heading" />
          <span className="pro-skeleton-line" />
          <span className="pro-skeleton-line" />
          <span className="pro-skeleton-line is-short" />
        </div>
      ))}
    </div>
  )
}

/* ---------------------------- Gated container ----------------------------- */

class GateErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) return this.props.fallback
    return this.props.children
  }
}

type GatedProps = {
  locale: Locale
  courseSlug: string
  lessonSlug: string
  lockedSectionTitles: string[]
  /** Renders the unlocked Pro sections with the page's own section markup. */
  renderSections: (sections: LessonSection[]) => ReactNode
}

/** Fire `paywall_viewed` once per lesson when the gate or banner settles. */
function usePaywallViewed(state: ProLessonState, tracking: GateTracking) {
  const sent = useRef<string | null>(null)
  const mode =
    state.status === 'locked'
      ? 'locked'
      : state.status === 'unlocked' && state.earlyAccess
        ? 'early_access'
        : null
  useEffect(() => {
    if (!mode) return
    const key = `${tracking.course}/${tracking.lesson}`
    if (sent.current === key) return
    sent.current = key
    trackEvent('paywall_viewed', {
      surface: 'pro_gate',
      course: tracking.course,
      lesson: tracking.lesson,
      mode,
    })
  }, [mode, tracking.course, tracking.lesson])
}

function GatedContainer({
  state,
  lockedSectionTitles,
  renderSections,
  tracking,
}: {
  state: ProLessonState
  lockedSectionTitles: string[]
  renderSections: (sections: LessonSection[]) => ReactNode
  tracking: GateTracking
}) {
  usePaywallViewed(state, tracking)
  const showGate = state.status === 'pending' || state.status === 'locked'
  const showSkeleton = state.status === 'pending' || state.status === 'loading'
  return (
    <div
      className={PRO_GATED_CLASS}
      id={PRO_GATED_ID}
      data-state={state.status}
    >
      {showGate ? (
        <ProGateCard
          lockedSectionTitles={lockedSectionTitles}
          signedIn={state.status === 'locked' && state.signedIn}
          tracking={tracking}
        />
      ) : null}
      {showSkeleton ? <ProLessonSkeleton /> : null}
      {state.status === 'unlocked' ? (
        <>
          {state.earlyAccess ? <EarlyAccessBanner tracking={tracking} /> : null}
          {renderSections(state.sections)}
        </>
      ) : null}
    </div>
  )
}

function LiveGatedContent(props: GatedProps) {
  const state = useProLessonState(props)
  return (
    <GatedContainer
      state={state}
      lockedSectionTitles={props.lockedSectionTitles}
      renderSections={props.renderSections}
      tracking={{ course: props.courseSlug, lesson: props.lessonSlug }}
    />
  )
}

/**
 * The Pro part of a lesson: gate, skeleton or the unlocked sections. If auth or
 * Convex are unavailable (no keys, network), it fails closed to the gate.
 */
export function ProGatedContent(props: GatedProps) {
  return (
    <GateErrorBoundary
      fallback={
        <GatedContainer
          state={{ status: 'locked', signedIn: false }}
          lockedSectionTitles={props.lockedSectionTitles}
          renderSections={props.renderSections}
          tracking={{ course: props.courseSlug, lesson: props.lessonSlug }}
        />
      }
    >
      <LiveGatedContent {...props} />
    </GateErrorBoundary>
  )
}

/* ------------------------------ Course banner ----------------------------- */

/**
 * Course-page callout for Pro courses. Billing live: preview is free, Pro
 * unlocks it all. Early access: everything is open, founding members lock in
 * the launch price.
 */
export function ProCourseCallout() {
  const { locale, t } = useTranslation()
  const live = useBillingStatus()
  const price = formatPrice(pricePerMonth('pro', 'year'), locale)
  const early = live === false
  return (
    <div className={early ? 'pro-course-callout is-early' : 'pro-course-callout'}>
      <div>
        <ProBadge />
        <strong>{early ? t('pro.courseEarlyTitle') : t('pro.courseTitle')}</strong>
        <p>{early ? t('pro.courseEarlyText') : t('pro.courseText')}</p>
        <small>{t('pro.priceNote', { price })}</small>
      </div>
      <a
        className="pro-gate-primary"
        href={
          early
            ? `${localizedPath('pricing', locale)}${FOUNDING_HASH}`
            : localizedPath('pricing', locale)
        }
        onClick={() =>
          trackEvent('paywall_cta_clicked', {
            surface: 'course_callout',
            cta: early ? 'join_founding' : 'unlock',
          })
        }
      >
        {early ? t('pro.earlyAccessCta') : t('pro.unlockCta')}
        <ArrowRight size={16} aria-hidden="true" />
      </a>
    </div>
  )
}
